import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { orderKeys } from '@/entities/order';
import { PERMISSIONS, teamApi, teamKeys, type FieldRole } from '@/entities/user';

import { reloadWorkspaceSession, type WorkspaceAccess } from '../useWorkspaceAccess';

/**
 * Wie er in de keuze staan: de actieve collega's en de openstaande uitnodigingen.
 *
 * Beide lijsten vragen `users.view`, wat kantoor heeft - precies wie inplant. Zonder
 * dat recht blijft alleen "Mijzelf" over. Een uitnodigingslijst die niet laadt,
 * houdt de keuze niet tegen: die is een extraatje.
 */
export function useInspectorChoices(access: WorkspaceAccess) {
    const enabled = !access.loading && access.can(PERMISSIONS.usersView);

    const members = useQuery({
        queryKey: teamKeys.activeMembers(),
        queryFn: () => teamApi.activeMembers(),
        staleTime: 60_000,
        enabled,
    });

    const invitations = useQuery({
        queryKey: teamKeys.invitations(),
        queryFn: () => teamApi.invitations(),
        staleTime: 60_000,
        enabled,
    });

    return {
        members: members.data ?? [],
        invitations: invitations.data ?? [],
        loading: access.loading || (enabled && members.isPending),
    };
}

/**
 * Jezelf een rol op locatie erbij geven. Daarna kent de sessie de nieuwe rol - dus
 * Mijn dag in het menu - en staan de planning en het team opnieuw klaar.
 */
export function useTakeOnFieldRole() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (role: FieldRole) => teamApi.takeOnRole(role),
        onSuccess: async () => {
            /* De rol staat er al; lukt het bijwerken van de sessie nu niet, dan bij het volgende opstarten. */
            await reloadWorkspaceSession(client).catch(() => undefined);

            void client.invalidateQueries({ queryKey: teamKeys.members() });
            void client.invalidateQueries({ queryKey: orderKeys.planningAll() });
            void client.invalidateQueries({ queryKey: ['dashboard'] });
        },
    });
}

/** Een collega uitnodigen als inspecteur, met zijn naam. */
export function useInviteInspector() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { name: string; email: string }) =>
            teamApi.invite({ name: input.name, email: input.email, role: 'inspector' }),
        onSuccess: () => client.invalidateQueries({ queryKey: teamKeys.invitations() }),
    });
}
